import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';

function ResizableImageView({ node, updateAttributes, selected, editor }) {
  const editable = editor?.isEditable;
  const { src, alt, width, alignment, uploading } = node.attrs;
  const imgRef = useRef(null);
  const [resizing, setResizing] = useState(false);
  const startX = useRef(0);
  const startW = useRef(0);
  const side = useRef('right');

  const onMouseDown = useCallback((e, handleSide) => {
    e.preventDefault();
    side.current = handleSide;
    startX.current = e.clientX;
    startW.current = imgRef.current?.offsetWidth || 200;
    setResizing(true);
  }, []);

  useEffect(() => {
    if (!resizing) return;
    const onMouseMove = (e) => {
      const dx = e.clientX - startX.current;
      const delta = side.current === 'left' ? -dx : dx;
      const newW = Math.max(60, Math.min(startW.current + delta, 800));
      updateAttributes({ width: newW });
    };
    const onMouseUp = () => setResizing(false);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [resizing, updateAttributes]);

  return (
    <NodeViewWrapper className={`tt-image-wrap tt-image-${alignment || 'center'}`} data-drag-handle="">
      <div className={`tt-image-container${selected && editable ? ' tt-image-selected' : ''}${uploading ? ' tt-image-uploading' : ''}`}
           style={{ width: width ? `${width}px` : undefined }}>
        {uploading ? (
          <div className="tt-image-loader">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="tt-spinner">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.2"/>
              <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span>Uploading…</span>
          </div>
        ) : (
          <img ref={imgRef} src={src} alt={alt || ''} draggable={false} />
        )}
        {selected && editable && (
          <>
            <div className="tt-image-handle tt-image-handle--left"
                 onMouseDown={(e) => onMouseDown(e, 'left')} />
            <div className="tt-image-handle tt-image-handle--right"
                 onMouseDown={(e) => onMouseDown(e, 'right')} />
            <div className="tt-image-align-bar">
              {['left', 'center', 'right'].map(a => (
                <button key={a}
                  type="button"
                  className={`tt-image-align-btn${alignment === a ? ' active' : ''}`}
                  onMouseDown={(e) => { e.preventDefault(); updateAttributes({ alignment: a }); }}
                  title={`Align ${a}`} aria-label={`Align ${a}`}>
                  {a === 'left' && <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 3h12M2 7h8M2 11h12M2 15h8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>}
                  {a === 'center' && <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 3h12M4 7h8M2 11h12M4 15h8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>}
                  {a === 'right' && <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 3h12M6 7h8M2 11h12M6 15h8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </NodeViewWrapper>
  );
}

export const ResizableImage = Node.create({
  name: 'resizableImage',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      width: { default: null },
      alignment: { default: 'center' },
      imageId: { default: null, rendered: false },
      uploading: { default: false, rendered: false },
    };
  },

  parseHTML() {
    return [{
      tag: 'img[src]',
      getAttrs(el) {
        const style = el.getAttribute('style') || '';
        const widthMatch = style.match(/width:\s*(\d+)px/);
        return {
          src: el.getAttribute('src'),
          alt: el.getAttribute('alt'),
          width: widthMatch ? parseInt(widthMatch[1], 10) : (el.getAttribute('width') ? parseInt(el.getAttribute('width'), 10) : null),
          alignment: el.getAttribute('data-alignment') || 'center',
        };
      },
    }];
  },

  renderHTML({ HTMLAttributes }) {
    const { alignment, width, ...rest } = HTMLAttributes;
    const style = [];
    if (width) style.push(`width:${width}px`);
    if (alignment === 'left') style.push('margin-right:auto');
    else if (alignment === 'right') style.push('margin-left:auto');
    else style.push('margin-left:auto', 'margin-right:auto');
    return ['img', mergeAttributes(rest, {
      style: style.join(';'),
      'data-alignment': alignment,
    })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView);
  },

  addCommands() {
    return {
      setImage: (attrs) => ({ commands }) => {
        return commands.insertContent({ type: this.name, attrs });
      },
    };
  },
});
